import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import SEO from '../components/SEO';
import {
  ArrowRight,
  Users,
  Zap,
  Target,
  CheckCircle2,
  Send,
  BarChart3,
  Sparkles,
  Shield,
  Cpu,
  ChevronRight,
  ChevronDown,
  Brain,
  Workflow,
  HelpCircle,
} from 'lucide-react';
import axios from 'axios';
import NeuralBackground from '../components/NeuralBackground';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Animated counter hook ── */
const useCountUp = (end, duration = 2000, suffix = '') => {
  const [value, setValue] = useState('0');
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const isNumeric = !isNaN(parseFloat(end));
          if (!isNumeric) { setValue(end); return; }
          const numEnd = parseFloat(end);
          const startTime = performance.now();
          const animate = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = Math.round(numEnd * eased);
            setValue(`${current}${suffix}`);
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration, suffix]);

  return [ref, value];
};

/* ── Motion variants ── */
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

/* ════════════════════════════════════════
   HOME PAGE
   ════════════════════════════════════════ */
const HomePage = () => {
  const mainRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const scrollRef = useRef(0);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollTop = window.scrollY;
          const docHeight = document.documentElement.scrollHeight - window.innerHeight;
          const progress = Math.min(scrollTop / (docHeight * 0.3), 1);
          if (Math.abs(progress - scrollRef.current) > 0.005) {
            scrollRef.current = progress;
            setScrollProgress(progress);
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div ref={mainRef}>
      <SEO
        title="KI-Entwicklung & kostenlose KI-Beratung | LAIzyones Magdeburg & Braunschweig"
        description="LAIzyones – Ihre Agentur für KI-Entwicklung und kostenlose KI-Beratung. Wir analysieren Ihre Prozesse kostenlos und entwickeln individuelle KI-Lösungen für den Mittelstand. Jetzt Beratungstermin vereinbaren."
        path="/"
        keywords="KI Entwicklung, KI Beratung, KI Beratung kostenlos, kostenlose KI-Beratung, KI Entwicklung Deutschland, KI-Entwicklung Unternehmen, kostenlose Prozessanalyse, KI Agentur Magdeburg, KI Beratung Braunschweig, Prozessautomatisierung KI, KI Lösungen Mittelstand, individuelle KI Entwicklung, KI Automatisierung Deutschland"
        breadcrumbs={[{ name: 'Startseite' }]}
        faqSchema={[
          { question: 'Was kostet KI-Beratung für Unternehmen?', answer: 'Die erste Prozessanalyse und Beratung ist bei LAIzyones komplett kostenlos und unverbindlich. Wir analysieren gemeinsam Ihre Prozesse und zeigen Ihnen konkret, wo KI-Entwicklung Potenzial hat. Danach richtet sich der Preis nach dem individuellen Projektumfang.' },
          { question: 'Wie lange dauert die Entwicklung einer KI-Lösung?', answer: 'Von der ersten kostenlosen Analyse bis zur produktiven KI-Lösung vergehen in der Regel 2–6 Wochen, abhängig von der Komplexität Ihres Projekts. In einem unverbindlichen Erstgespräch können wir den Zeitrahmen für Ihr Vorhaben einschätzen.' },
          { question: 'Ist KI auch für kleine Unternehmen sinnvoll?', answer: 'Ja. Gerade kleine Unternehmen profitieren von KI-Automatisierung, da ein einzelner KI-Agent die Arbeit von 2–3 Mitarbeitern bei repetitiven Aufgaben übernehmen kann.' },
          { question: 'Sind die KI-Lösungen von LAIzyones DSGVO-konform?', answer: 'Ja, alle unsere Lösungen sind vollständig DSGVO-konform. Daten werden in Deutschland verarbeitet und unterliegen den höchsten Sicherheitsstandards.' },
          { question: 'Wo ist LAIzyones tätig?', answer: 'Unser Schwerpunkt liegt in Magdeburg und Braunschweig, wir arbeiten jedoch mit Unternehmen in ganz Deutschland zusammen – vor Ort und remote.' },
          { question: 'Welche Branchen profitieren am meisten von KI-Automatisierung?', answer: 'Grundsätzlich profitiert jede Branche mit repetitiven, datenintensiven Prozessen. Besonders hohe Effizienzgewinne sehen wir in der Fertigungsindustrie, im Handel, bei Finanzdienstleistungen, im Gesundheitswesen und in der Logistik.' },
        ]}
      />
      <HeroSection scrollProgress={scrollProgress} />
      <StatsMarquee />
      {/* Dark → Light bridge */}
      <div className="h-24" style={{ background: 'linear-gradient(to bottom, #020405, #F8FAFC)' }} />
      <WhyChooseUs />
      {/* Light → Dark bridge */}
      <div className="h-24" style={{ background: 'linear-gradient(to bottom, #F8FAFC, #020405)' }} />
      <MissionSection />
      {/* Dark → Light bridge */}
      <div className="h-24" style={{ background: 'linear-gradient(to bottom, #020405, #F8FAFC)' }} />
      <ServicesOverview />
      {/* Light → Dark bridge */}
      <div className="h-24" style={{ background: 'linear-gradient(to bottom, #F8FAFC, #020405)' }} />
      <FAQSection />
      {/* Dark → Light bridge */}
      <div className="h-24" style={{ background: 'linear-gradient(to bottom, #020405, #F8FAFC)' }} />
      <CTASection />
      {/* Dark → Light bridge */}
      <div className="h-24" style={{ background: 'linear-gradient(to bottom, #020405, #F8FAFC)' }} />
      <ContactSection />
      {/* Light → Dark bridge (footer) */}
      <div className="h-24" style={{ background: 'linear-gradient(to bottom, #F8FAFC, #020405)' }} />
    </div>
  );
};

/* ════════════════════════════════════════
   HERO
   ════════════════════════════════════════ */
const HeroSection = ({ scrollProgress = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center overflow-hidden"
    >
      {/* Neural Network Background */}
      <NeuralBackground scrollProgress={scrollProgress} />

      {/* Kinetic background text */}
      <div className="absolute inset-0 z-[1] flex items-end overflow-hidden pointer-events-none">
        <div className="kinetic-bg-text">
          INTELLIGENZ&nbsp;&nbsp;ZUKUNFT&nbsp;&nbsp;AUTOMATISIERUNG&nbsp;&nbsp;INTELLIGENZ&nbsp;&nbsp;ZUKUNFT&nbsp;&nbsp;AUTOMATISIERUNG&nbsp;&nbsp;
        </div>
      </div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 z-[2]" style={{
        background: 'linear-gradient(to bottom, rgba(2,4,5,0.8) 0%, rgba(2,4,5,0.4) 30%, rgba(2,4,5,0.3) 50%, rgba(2,4,5,0.5) 75%, rgba(2,4,5,0.95) 100%)'
      }} />

      {/* Radial glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[2]">
        <div className="radial-glow" />
      </div>

      {/* Content */}
      <div className="container-custom relative z-10 pt-32 pb-20">
        <motion.div
          className="max-w-4xl"
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={staggerContainer}
        >
          {/* Badge */}
          <motion.div variants={fadeUp} custom={0}>
            <div className="badge mb-8" style={{ background: 'rgba(16, 185, 129, 0.1)', borderColor: 'rgba(16, 185, 129, 0.25)', color: '#4ADE80' }}>
              <Sparkles className="w-3.5 h-3.5" />
              KI-Lösungen der nächsten Generation
            </div>
          </motion.div>

          {/* Headline — SEO H1 */}
          <motion.h1
            variants={fadeUp} custom={1}
            className="heading-xl text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white mb-6"
          >
            KI-Lösungen für Unternehmen{' '}
            <br className="hidden sm:block" />
            – Prozesse{' '}
            <span className="text-shimmer">automatisieren</span>
            <br />
            & Kosten senken
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            variants={fadeUp} custom={2}
            className="body-text text-base md:text-lg text-gray-400 max-w-xl mb-10 leading-relaxed"
          >
            Ihre Agentur für KI-Entwicklung in Magdeburg, Braunschweig und ganz Deutschland.
            Wir analysieren Ihre Geschäftsprozesse <strong className="text-gray-200">kostenlos</strong> und 
            entwickeln maßgeschneiderte KI-Lösungen — messbar, nachhaltig, DSGVO-konform.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row gap-4 mb-16">
            <button
              data-testid="hero-cta-consultation"
              className="btn-primary text-base"
              onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <span>Kostenlose Analyse vereinbaren</span>
              <ArrowRight className="w-5 h-5 relative z-10" />
            </button>
            <Link
              to="/produkte"
              data-testid="hero-cta-products"
              className="btn-secondary text-base"
            >
              Unsere KI-Produkte
            </Link>
          </motion.div>

          {/* Trust indicators */}
          <motion.div
            variants={fadeUp} custom={4}
            className="glass-card-dark py-5 px-6 inline-flex flex-wrap items-center gap-x-8 gap-y-3"
            style={{ background: 'rgba(11, 18, 33, 0.5)' }}
          >
            {[
              { icon: Shield, label: 'DSGVO-konform' },
              { icon: Cpu, label: 'Made in Germany' },
              { icon: Users, label: 'Enterprise-ready' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-emerald-400" />
                <span className="text-sm font-medium text-white/90">{label}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

/* ════════════════════════════════════════
   STATS MARQUEE (Infinite scrolling ticker)
   ════════════════════════════════════════ */
const StatsMarquee = () => {
  const stats = [
    { value: '30%', label: 'Zeitersparnis' },
    { value: '2x', label: 'Produktivität' },
    { value: '50%', label: 'Kostensenkung' },
    { value: '24/7', label: 'Verfügbarkeit' },
    { value: '100+', label: 'Integrationen' },
    { value: '99.9%', label: 'Uptime' },
  ];

  const doubled = [...stats, ...stats];

  return (
    <section data-testid="stats-marquee" className="section-dark py-8 overflow-hidden border-y border-white/5">
      <div className="marquee-track">
        {doubled.map((s, i) => (
          <div key={i} className="flex items-center gap-3 px-10 shrink-0">
            <span className="text-2xl md:text-3xl font-bold font-['Space_Grotesk'] text-shimmer">{s.value}</span>
            <span className="text-sm text-gray-500 font-medium uppercase tracking-wider">{s.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ════════════════════════════════════════
   WHY CHOOSE US — Bento Grid
   ════════════════════════════════════════ */
const WhyChooseUs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });

  const reasons = [
    {
      icon: Zap, title: 'Schnelle Implementierung',
      description: 'Von der ersten Analyse bis zur produktiven KI-Lösung in wenigen Wochen — nicht Monaten.',
      large: true,
    },
    {
      icon: Target, title: 'Maßgeschneidert',
      description: 'KI-Tools, exakt auf Ihre Geschäftsprozesse zugeschnitten. Keine Standard-Software.',
    },
    {
      icon: Shield, title: '100% DSGVO-konform',
      description: 'Ihre Daten bleiben in Deutschland. Höchste Sicherheitsstandards und vollständige Compliance.',
    },
    {
      icon: Users, title: 'Persönliche Betreuung',
      description: 'Ein dediziertes Team begleitet Sie von der Konzeption bis zur erfolgreichen Integration.',
    },
    {
      icon: Brain, title: 'State-of-the-Art KI',
      description: 'Neueste Modelle und Technologien, optimiert für Ihren Use-Case. Immer aktuell.',
      large: true,
    },
  ];

  return (
    <section
      id="warum-wir"
      data-testid="why-choose-section"
      className="section-light py-24 md:py-32"
      ref={ref}
    >
      <div className="container-custom">
        {/* Header */}
        <motion.div
          className="max-w-3xl mb-16"
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={staggerContainer}
        >
          <motion.div variants={fadeUp}><div className="badge mb-4">Warum LAIzyones</div></motion.div>
          <motion.h2 variants={fadeUp} custom={1} className="heading-lg text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-4">
            Ihre KI-Agentur für <span className="gradient-text">Prozessautomatisierung</span>
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="body-text text-base md:text-lg text-gray-500">
            Als erfahrene KI-Beratung unterstützen wir kleine und mittelständische Unternehmen dabei, Geschäftsprozesse mit künstlicher Intelligenz zu automatisieren — von der kostenlosen Erstanalyse bis zur fertigen Lösung.
          </motion.p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          className="bento-grid"
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={staggerContainer}
        >
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <motion.div
                key={index}
                variants={scaleIn}
                data-testid={`reason-${index}`}
                className={`bento-item group ${reason.large ? 'bento-lg' : ''}`}
              >
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-50 to-cyan-50 flex items-center justify-center mb-5 group-hover:from-emerald-100 group-hover:to-cyan-100 transition-colors duration-300">
                  <Icon className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-['Space_Grotesk'] font-semibold text-xl text-gray-900 mb-2">
                  {reason.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {reason.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

/* ════════════════════════════════════════
   MISSION — Split Layout
   ════════════════════════════════════════ */
const MissionSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section
      ref={ref}
      data-testid="mission-section"
      className="section-dark py-24 md:py-32 relative overflow-hidden"
    >
      {/* Subtle radial glow */}
      <div className="absolute top-1/2 right-0 translate-x-1/4 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.08), transparent 70%)' }}
      />

      <div className="container-custom relative z-10">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={staggerContainer}
        >
          {/* Content */}
          <div>
            <motion.div variants={fadeUp}><div className="badge mb-4" style={{ color: '#4ADE80', background: 'rgba(16,185,129,0.1)', borderColor: 'rgba(16,185,129,0.2)' }}>Unsere Mission</div></motion.div>
            <motion.h2 variants={fadeUp} custom={1} className="heading-lg text-3xl md:text-4xl lg:text-5xl text-white mb-6">
              KI-Entwicklung für den{' '}
              <span className="text-shimmer">Mittelstand</span>
            </motion.h2>
            <motion.div variants={fadeUp} custom={2} className="space-y-4">
              <p className="body-text text-base md:text-lg text-gray-400">
                Unsere Mission: <strong className="text-gray-200">Individuelle KI-Entwicklung</strong> aus
                den Laboren der Tech-Giganten in den deutschen Mittelstand bringen — nach Magdeburg, Braunschweig und in ganz Deutschland.
              </p>
              <p className="body-text text-base md:text-lg text-gray-400">
                Wir entwickeln <strong className="text-gray-200">praxisnahe KI-Lösungen</strong>, die sich
                nahtlos in bestehende Workflows integrieren und sofort messbare Ergebnisse liefern. Starten Sie mit einer <strong className="text-gray-200">kostenlosen Prozessanalyse</strong> — wir zeigen Ihnen, wo KI in Ihrem Unternehmen den größten Hebel hat.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} custom={3} className="mt-8 space-y-3">
              {[
                'Kostenlose Erstanalyse Ihrer Prozesse',
                'Individuelle KI-Entwicklung statt Standardlösungen',
                'Messbare ROI-Steigerung garantiert',
              ].map((point, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span className="text-gray-300 font-medium text-sm">{point}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Visual card with parallax */}
          <motion.div style={{ y }}>
            <motion.div variants={scaleIn} className="glass-card-dark p-8 relative">
              {/* Top beam */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/20 mb-4">
                  <Target className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="font-['Space_Grotesk'] font-bold text-2xl text-white mb-2">Unser Ziel</h3>
              </div>
              <p className="text-gray-400 text-center text-base leading-relaxed">
                Bis 2027 möchten wir <span className="text-emerald-400 font-semibold">1.000
                mittelständische Unternehmen</span> dabei unterstützen, ihre Prozesse mit
                KI zu optimieren und ihre Wettbewerbsfähigkeit nachhaltig zu steigern.
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

/* ════════════════════════════════════════
   SERVICES OVERVIEW — Links to dedicated pages
   ════════════════════════════════════════ */
const ServicesOverview = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const services = [
    {
      icon: BarChart3,
      title: 'Prozessanalyse & Automatisierung',
      description: 'Wir analysieren Ihre Geschäftsprozesse kostenlos und identifizieren Potenziale für KI-Automatisierung — vereinbaren Sie einen unverbindlichen Termin.',
      href: '/prozessanalyse',
      keywords: 'Kostenlose Prozessanalyse',
    },
    {
      icon: Brain,
      title: 'Individuelle KI-Entwicklung',
      description: 'Maßgeschneiderte KI-Entwicklung, exakt auf Ihre Abläufe zugeschnitten: Chatbots, Datenanalyse, Workflow-Automatisierung und mehr.',
      href: '/ki-tool-entwicklung',
      keywords: 'KI-Entwicklung',
    },
    {
      icon: Sparkles,
      title: 'Geschäftsideen & Start-Up Umsetzung',
      description: 'Von der Idee zum marktreifen Produkt — wir begleiten Gründer und Unternehmen mit KI-gestützter MVP-Entwicklung und Marktvalidierung.',
      href: '/geschaeftsideen',
      keywords: 'Geschäftsideen entwickeln',
    },
    {
      icon: Workflow,
      title: 'Systeme & Landingpages',
      description: 'Professionelle Websites, conversion-optimierte Landingpages und Web-Applikationen — SEO-optimiert, mobil-first und schnell.',
      href: '/systeme-landingpages',
      keywords: 'Landingpages erstellen',
    },
  ];

  const scrollToContact = () => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section
      ref={ref}
      data-testid="services-overview"
      className="section-light py-24 md:py-32"
    >
      <div className="container-custom">
        {/* Header */}
        <motion.div
          className="text-center max-w-3xl mx-auto mb-16"
          initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={staggerContainer}
        >
          <motion.div variants={fadeUp}><div className="badge mb-4">Unsere Leistungen</div></motion.div>
          <motion.h2 variants={fadeUp} custom={1} className="heading-lg text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-4">
            Vier Services, ein Ziel: <span className="gradient-text">Ihre KI-Entwicklung</span>
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="body-text text-base md:text-lg text-gray-500">
            Von der kostenlosen Prozessanalyse bis zur fertigen KI-Entwicklung — wir begleiten Sie auf dem gesamten Weg.
            Vereinbaren Sie einen Termin und lassen Sie uns gemeinsam schauen, welche Prozesse in Ihrem Unternehmen optimiert werden können.
          </motion.p>
        </motion.div>

        {/* Service Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12"
          initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={staggerContainer}
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div key={index} variants={scaleIn}>
                <Link
                  to={service.href}
                  data-testid={`service-card-${index}`}
                  className="glass-card-light group p-6 md:p-8 block hover:border-emerald-200 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-50 to-cyan-50 flex items-center justify-center mb-5 group-hover:from-emerald-100 group-hover:to-cyan-100 transition-colors duration-300">
                    <Icon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="font-['Space_Grotesk'] font-semibold text-xl text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{service.description}</p>
                  <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold text-sm group-hover:gap-2 transition-all duration-300">
                    Mehr erfahren <ChevronRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center"
          initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={staggerContainer}
        >
          <motion.div variants={fadeUp}>
            <button
              onClick={scrollToContact}
              data-testid="services-cta"
              className="btn-primary text-base"
            >
              <span>Kostenlose Beratung anfragen</span>
              <ArrowRight className="w-5 h-5 relative z-10" />
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

/* ════════════════════════════════════════
   FAQ — SEO-optimized Accordion
   ════════════════════════════════════════ */
const FAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'Was kostet KI-Beratung für Unternehmen?',
      answer: 'Die erste Prozessanalyse ist bei LAIzyones kostenlos. Danach richtet sich der Preis nach dem individuellen Projektumfang. Typische Projekte starten ab wenigen tausend Euro. In einem unverbindlichen Erstgespräch ermitteln wir gemeinsam das Einsparpotenzial und den konkreten Investitionsbedarf für Ihr Unternehmen.',
    },
    {
      question: 'Wie lange dauert die Implementierung einer KI-Lösung?',
      answer: 'Von der ersten Analyse bis zur produktiven KI-Lösung vergehen in der Regel 2–6 Wochen, abhängig von der Komplexität Ihres Projekts. Einfache Automatisierungen wie E-Mail-Verarbeitung oder Datenextraktion können oft schon nach wenigen Tagen produktiv sein.',
    },
    {
      question: 'Ist KI auch für kleine Unternehmen sinnvoll?',
      answer: 'Ja, absolut. Gerade kleine Unternehmen profitieren enorm von KI-Automatisierung, da ein einzelner KI-Agent die Arbeit von 2–3 Mitarbeitern bei repetitiven Aufgaben übernehmen kann. So können Sie sich auf Ihr Kerngeschäft konzentrieren, ohne zusätzliches Personal einzustellen.',
    },
    {
      question: 'Sind die KI-Lösungen von LAIzyones DSGVO-konform?',
      answer: 'Ja, alle unsere Lösungen sind vollständig DSGVO-konform. Daten werden in Deutschland verarbeitet und unterliegen den höchsten Sicherheitsstandards. Wir arbeiten ausschließlich mit europäischen und deutschen Hosting-Partnern und beraten Sie auch bei datenschutzrechtlichen Fragen.',
    },
    {
      question: 'Wo ist LAIzyones tätig?',
      answer: 'Unser Schwerpunkt liegt in Magdeburg und Braunschweig, wir arbeiten jedoch mit Unternehmen in ganz Deutschland zusammen — vor Ort und remote. Dank digitaler Zusammenarbeit ist die geografische Entfernung kein Hindernis für eine erfolgreiche KI-Implementierung.',
    },
    {
      question: 'Welche Branchen profitieren am meisten von KI-Automatisierung?',
      answer: 'Grundsätzlich profitiert jede Branche mit repetitiven, datenintensiven Prozessen. Besonders hohe Effizienzgewinne sehen wir in der Fertigungsindustrie, im Handel, bei Finanzdienstleistungen, im Gesundheitswesen und in der Logistik. In unserem kostenlosen KI-Assessment identifizieren wir das konkrete Potenzial für Ihre Branche.',
    },
  ];

  return (
    <section
      ref={ref}
      data-testid="faq-section"
      className="section-dark py-24 md:py-32 relative overflow-hidden"
    >
      <div className="absolute inset-0 grid-pattern opacity-20 z-0" />
      <div className="container-custom relative z-10">
        <motion.div
          className="text-center max-w-3xl mx-auto mb-16"
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={staggerContainer}
        >
          <motion.div variants={fadeUp}>
            <div className="badge mb-4" style={{ color: '#4ADE80', background: 'rgba(16,185,129,0.1)', borderColor: 'rgba(16,185,129,0.2)' }}>
              <HelpCircle className="w-3.5 h-3.5" />
              Häufige Fragen
            </div>
          </motion.div>
          <motion.h2 variants={fadeUp} custom={1} className="heading-lg text-3xl md:text-4xl lg:text-5xl text-white mb-4">
            Antworten auf Ihre Fragen zur{' '}
            <span className="text-shimmer">KI-Beratung</span>
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="body-text text-base md:text-lg text-gray-400">
            Die wichtigsten Fragen und Antworten rund um KI-Lösungen, Kosten und Implementierung für Ihr Unternehmen.
          </motion.p>
        </motion.div>

        <motion.div
          className="max-w-3xl mx-auto space-y-3"
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={staggerContainer}
        >
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              custom={index}
              className="glass-card-dark overflow-hidden"
            >
              <button
                data-testid={`faq-toggle-${index}`}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-5 md:p-6 text-left group"
              >
                <h3 className="font-['Space_Grotesk'] font-semibold text-base md:text-lg text-white pr-4 group-hover:text-emerald-300 transition-colors">
                  {faq.question}
                </h3>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180 text-emerald-400' : ''
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  openIndex === index ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <p className="px-5 md:px-6 pb-5 md:pb-6 text-gray-400 text-sm md:text-base leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* ════════════════════════════════════════
   CTA — Radiant
   ════════════════════════════════════════ */
const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section ref={ref} className="section-dark py-24 md:py-32 relative overflow-hidden">
      {/* Radial glows */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(16, 185, 129, 0.12) 0%, transparent 60%)' }}
      />

      <motion.div
        className="container-custom relative z-10 text-center max-w-3xl mx-auto"
        initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={staggerContainer}
      >
        <motion.h2 variants={fadeUp} className="heading-lg text-3xl md:text-4xl lg:text-5xl text-white mb-6">
          Bereit für Ihre individuelle{' '}
          <span className="text-shimmer">KI-Entwicklung?</span>
        </motion.h2>
        <motion.p variants={fadeUp} custom={1} className="text-gray-400 text-base md:text-lg mb-10 leading-relaxed">
          Vereinbaren Sie jetzt Ihren kostenlosen Beratungstermin. Wir analysieren gemeinsam
          Ihre Prozesse und zeigen Ihnen konkret, wo KI-Entwicklung Ihr Unternehmen voranbringt — unverbindlich und ohne versteckte Kosten.
        </motion.p>
        <motion.div variants={fadeUp} custom={2} className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            data-testid="cta-contact-btn"
            onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-primary text-base"
          >
            <span>Kostenlosen Termin vereinbaren</span>
            <ArrowRight className="w-5 h-5 relative z-10" />
          </button>
          <Link to="/produkte" className="btn-secondary text-base inline-flex items-center justify-center gap-2">
            Produkte ansehen
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
};

/* ════════════════════════════════════════
   CONTACT
   ════════════════════════════════════════ */
const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Leider konnte Ihre Nachricht nicht gesendet werden. Bitte versuchen Sie es erneut oder schreiben Sie uns direkt an info@laizyones.de');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      ref={ref}
      className="section-light py-24 md:py-32"
    >
      <div className="container-custom">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-16"
          initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={staggerContainer}
        >
          {/* Left */}
          <div>
            <motion.div variants={fadeUp}><div className="badge mb-4">Kontakt</div></motion.div>
            <motion.h2 variants={fadeUp} custom={1} className="heading-lg text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-4">
              Kostenlose <span className="gradient-text">Beratung & Analyse</span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="body-text text-base md:text-lg text-gray-500 mb-8">
              Vereinbaren Sie jetzt einen Termin für Ihre kostenlose Prozessanalyse. Wir schauen gemeinsam, welche Prozesse in Ihrem Unternehmen durch KI-Entwicklung optimiert werden können — unverbindlich und ohne Risiko.
            </motion.p>

            <motion.div variants={fadeUp} custom={3} className="space-y-4">
              {[
                'Kostenlose Erstanalyse Ihrer Unternehmensprozesse',
                'Konkrete Potenziale für KI-Entwicklung aufzeigen',
                'Unverbindlicher Austausch mit unseren KI-Experten',
                'Individueller Fahrplan für Ihre KI-Transformation',
              ].map((benefit, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span className="text-gray-600 text-sm">{benefit}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right — Form */}
          <motion.div variants={fadeUp} custom={2}>
            {isSubmitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-10 bg-emerald-50 rounded-2xl border border-emerald-200">
                <CheckCircle2 className="w-14 h-14 text-emerald-600 mb-5" />
                <h3 className="text-2xl font-['Space_Grotesk'] font-semibold text-gray-900 mb-3">Nachricht gesendet!</h3>
                <p className="text-gray-600 text-sm">Vielen Dank. Wir melden uns innerhalb von 24 Stunden.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Name *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Ihr Name" required data-testid="contact-name" className="form-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">E-Mail *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="ihre@email.de" required data-testid="contact-email" className="form-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Unternehmen</label>
                  <input type="text" name="company" value={formData.company} onChange={handleChange} placeholder="Ihr Unternehmen" data-testid="contact-company" className="form-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Nachricht *</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Beschreiben Sie kurz Ihr Anliegen..." rows={4} required data-testid="contact-message" className="form-input resize-none" />
                </div>
                <button type="submit" disabled={isSubmitting} data-testid="contact-submit" className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                  <span>{isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}</span>
                  {!isSubmitting && <Send className="w-5 h-5 relative z-10" />}
                </button>
                {submitError && (
                  <p className="text-red-500 text-sm mt-3 text-center">{submitError}</p>
                )}
              </form>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HomePage;
